/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Grid, Thumbnail } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Filter from 'src/components/containers/analytics-v2/filter';
import Chart from 'src/components/views/analytics-v2/display/chart';
import AnalyticsTable from 'src/components/views/analytics-v2/display/table';
import AnalyticsChartTable from 'src/components/views/analytics-v2/display/chart-table';
import noProductImage from 'src/assets/images/product/no-product-image.png';

import { routes } from 'src/routes';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { formatNumberInThousand, optimizeImage } from 'src/helpers';
import {
  buildCompareDateRange,
  buildDateRangeFromDate,
  displayDateRangeV2
} from 'src/helpers/analytics-helpers';
import {
  QUERY_EXECUTION_KEY,
  SEARCH_ANALYTIC_METRIC,
  TOP_ITEM_IN_OVERVIEW,
  TOP_ITEM_IN_DETAIL,
  FEATURES,
  MAX_NUM_X_AXIS_POINTS_SEARCH_CHART,
  limitXAxisPoint
} from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';
import { formatMoney } from 'src/helpers/money-format';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {};

const fromDateStateName = 'searchAnalyticsFromDate';
const toDateStateName = 'searchAnalyticsToDate';
const compareStateName = 'searchAnalyticsCompare';
class SearchAnalyticsOverview extends Component<ComposedProps, State> {
  state = {};

  componentDidMount() {
    const { fromDate, toDate, compare } = this.props;
    if (fromDate && toDate) {
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);

      this.fetchAll(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const { fromDate: nextFromDate, toDate: nextToDate, compare: nextCompare } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      onAbortApiAnalytic();
      setTimeout(() => {
        this.fetchAll(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  processHideLandMarks = categories => {
    const landmarksConversionRate = document.querySelectorAll(
      '.Search-Conversion-rate .apexcharts-xaxis-tick'
    );
    landmarksConversionRate.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksClickRate = document.querySelectorAll(
      '.Search-Click-rate .apexcharts-xaxis-tick'
    );
    landmarksClickRate.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksTop = document.querySelectorAll('.Search-Top .apexcharts-xaxis-tick');
    landmarksTop.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksRevenue = document.querySelectorAll('.Search-Revenue .apexcharts-xaxis-tick');
    landmarksRevenue.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksWithoutClick = document.querySelectorAll(
      '.Search-Without-click .apexcharts-xaxis-tick'
    );
    landmarksWithoutClick.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksWithoutResult = document.querySelectorAll(
      '.Search-Without-result .apexcharts-xaxis-tick'
    );
    landmarksWithoutResult.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
  };

  render() {
    const {
      fromDate,
      toDate,
      compare,
      searchesWithoutResultRate,
      searchesWithoutResultRateCompared,
      searchesWithoutResultRatePercent,
      searchesWithoutResultRatePercentChange,
      searchesWithoutResultRateLoading,
      searchTermsWithoutResult,
      searchTermsWithoutResultLoading,

      searchesClickRate,
      searchesClickRateCompared,
      searchesClickRatePercent,
      searchesClickRatePercentChange,
      searchesClickRateLoading,

      totalSearchesWithoutClick,
      totalSearchesWithoutClickCompared,
      totalSearchesWithoutClickPercent,
      totalSearchesWithoutClickPercentChange,
      totalSearchesWithoutClickLoading,

      topSearchTermsWithoutClick,
      topSearchTermsWithoutClickLoading,

      conversionRate,
      conversionRateCompared,
      conversionRateLoading,
      conversionRatePercent,
      conversionRatePercentChange,

      searchRevenue,
      searchRevenueCompared,
      searchRevenueLoading,
      searchRevenueTotal,
      searchRevenuePercentChange,

      totalSearches,
      totalSearchesCompared,
      totalSearchesLoading,
      totalSearchesNumber,
      totalSearchesPercentChange,

      topSearchTerms,
      topSearchTermsLoading,

      topSearchTermsWithClick,
      topSearchTermsWithClickLoading,

      topProductsOnSearch,
      topProductsOnSearchLoading,
      performActionSurvey,
      openReviewAddonModal,
      moneyFormat,
      i18n
    } = this.props;
    const displayedSourceDate = displayDateRangeV2(fromDate, toDate);
    const displayedComparedDate = compare
      ? buildCompareDateRange(fromDate, toDate, buildDateRangeFromDate(fromDate, toDate), 'v2')
      : '';

    const {
      series: searchesWithoutResultRateSeries,
      categories: searchesWithoutResultRateCategories
    } = this.buildChartData(
      searchesWithoutResultRate,
      searchesWithoutResultRateCompared,
      displayedSourceDate,
      displayedComparedDate,
      null,
      MAX_NUM_X_AXIS_POINTS_SEARCH_CHART
    );

    const { series: searchesClickRateSeries, categories: searchesClickRateCategories } =
      this.buildChartData(
        searchesClickRate,
        searchesClickRateCompared,
        displayedSourceDate,
        displayedComparedDate,
        null,
        MAX_NUM_X_AXIS_POINTS_SEARCH_CHART
      );

    const {
      series: totalSearchesWithoutClickSeries,
      categories: totalSearchesWithoutClickCategories
    } = this.buildChartData(
      totalSearchesWithoutClick,
      totalSearchesWithoutClickCompared,
      displayedSourceDate,
      displayedComparedDate,
      null,
      MAX_NUM_X_AXIS_POINTS_SEARCH_CHART
    );

    const { series: conversionRateSeries, categories: conversionRateCategories } =
      this.buildChartData(
        conversionRate,
        conversionRateCompared,
        displayedSourceDate,
        displayedComparedDate,
        /* customFieldTotal = */ 'conversionRate',
        MAX_NUM_X_AXIS_POINTS_SEARCH_CHART
      );

    const { series: searchRevenueSeries, categories: searchRevenueCategories } =
      this.buildChartData(
        searchRevenue,
        searchRevenueCompared,
        displayedSourceDate,
        displayedComparedDate,
        null,
        MAX_NUM_X_AXIS_POINTS_SEARCH_CHART
      );

    const { series: searchesRateSeries, categories: searchesRateCategories } = this.buildChartData(
      totalSearches,
      totalSearchesCompared,
      displayedSourceDate,
      displayedComparedDate,
      null,
      MAX_NUM_X_AXIS_POINTS_SEARCH_CHART
    );

    setTimeout(() => {
      this.processHideLandMarks(searchesWithoutResultRateCategories);
    }, 1000);

    return (
      <div className="BC-Page BC-Page__SearchAnalytics__Overview">
        <Filter
          feature={FEATURES.SEARCH}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={SEARCH_ANALYTIC_METRIC.OVERVIEW}
          openReviewAddonModal={openReviewAddonModal}
        />
        <div>
          <Grid columns={{ xs: 1 }}>
            {/* Total searches */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Chart
                isLimitxAxis
                unlimitedYAxis
                className="Search-Revenue"
                type="line"
                loading={searchRevenueLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.SearchAnalytics.totalSearchRevenue_cardTitle'
                )}
                tooltip={i18n.translate('AdminHTML.Analytics.Common.tooltip_ChartSearchRevenue')}
                total={formatMoney(searchRevenueTotal, moneyFormat)}
                percentGrowthChange={searchRevenuePercentChange}
                series={searchRevenueSeries}
                categories={searchRevenueCategories}
              />
            </Grid.Cell>
            {/* Total searches */}

            {/* Conversion rate */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Chart
                isLimitxAxis
                className="Search-Conversion-rate"
                type="line"
                suffixOfValue="%"
                loading={conversionRateLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.SearchAnalytics.conversionRate_cardTitle'
                )}
                tooltip={i18n.translate('AdminHTML.Analytics.Common.tooltip_ChartConversionRate')}
                total={conversionRatePercent}
                percentGrowthChange={conversionRatePercentChange}
                series={conversionRateSeries}
                categories={conversionRateCategories}
              />
            </Grid.Cell>
            {/* Conversion rate */}

            {/* Total Searches */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartTable
                loading={totalSearchesLoading || topSearchTermsLoading}
                title={i18n.translate('AdminHTML.Analytics.SearchAnalytics.searches_cardTitle')}
                tooltip={i18n.translate('AdminHTML.Analytics.Common.tooltip_ChartSearches')}
                total={totalSearchesNumber}
                percentGrowthChange={totalSearchesPercentChange}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    unlimitedYAxis
                    className="Search-Top"
                    transparent
                    type="line"
                    height="270px"
                    title={i18n.translate('AdminHTML.Analytics.Common.chart')}
                    series={searchesRateSeries}
                    categories={searchesRateCategories}
                  />
                }
                table={
                  <AnalyticsTable
                    smallRow
                    transparent
                    title={i18n.translate(
                      'AdminHTML.Analytics.SearchAnalytics.topSearches_tableTitle'
                    )}
                    linkDetail={routes.analytics.searches()}
                    dataTableProps={{
                      headings: [
                        i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                        i18n.translate('AdminHTML.Analytics.Common.searchTerm'),
                        i18n.translate('AdminHTML.Analytics.Common.totalSearches')
                      ],
                      rows: this.buildFallbackDataAnalyticsTable(
                        topSearchTerms
                          .slice(0, TOP_ITEM_IN_OVERVIEW)
                          .map((item, index) => [
                            index + 1,
                            item.searchTerm,
                            formatNumberInThousand(item.totalSearch)
                          ])
                      ),
                      columnContentTypes: ['text', 'text', 'numeric']
                    }}
                  />
                }
              />
            </Grid.Cell>
            {/* Total Searches */}

            {/* Searches */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartTable
                loading={searchesClickRateLoading || topSearchTermsWithClickLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.SearchAnalytics.searchesWithClicks_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.Common.tooltip_ChartSearchesWithClick'
                )}
                total={searchesClickRatePercent}
                percentGrowthChange={searchesClickRatePercentChange}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="Search-Click-rate"
                    suffixOfValue="%"
                    transparent
                    type="line"
                    height="270px"
                    title={i18n.translate('AdminHTML.Analytics.Common.chart')}
                    series={searchesClickRateSeries}
                    categories={searchesClickRateCategories}
                  />
                }
                table={
                  <AnalyticsTable
                    smallRow
                    transparent
                    title={i18n.translate(
                      'AdminHTML.Analytics.SearchAnalytics.topSearchesWithClick_tableTitle'
                    )}
                    linkDetail={routes.analytics.searchesWithClick()}
                    dataTableProps={{
                      headings: [
                        i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                        i18n.translate('AdminHTML.Analytics.Common.searchTerm'),
                        i18n.translate('AdminHTML.Analytics.Common.totalSearchesWithClick')
                      ],
                      rows: this.buildFallbackDataAnalyticsTable(
                        topSearchTermsWithClick
                          .slice(0, TOP_ITEM_IN_OVERVIEW)
                          .map((item, index) => [
                            index + 1,
                            item.searchTerm,
                            formatNumberInThousand(item.totalClicks)
                          ])
                      ),
                      columnContentTypes: ['text', 'text', 'numeric']
                    }}
                  />
                }
              />
            </Grid.Cell>
            {/* Searches */}

            {/* Searches without clicks */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartTable
                inverse
                loading={totalSearchesWithoutClickLoading || topSearchTermsWithoutClickLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.SearchAnalytics.searchesWithoutClicks_cardTitle'
                )}
                total={totalSearchesWithoutClickPercent}
                percentGrowthChange={totalSearchesWithoutClickPercentChange}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.Common.tooltip_ChartSearchesWithoutClick'
                )}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="Search-Without-click"
                    transparent
                    type="line"
                    height="270px"
                    suffixOfValue="%"
                    title={i18n.translate('AdminHTML.Analytics.Common.chart')}
                    series={totalSearchesWithoutClickSeries}
                    categories={totalSearchesWithoutClickCategories}
                  />
                }
                table={
                  <AnalyticsTable
                    smallRow
                    transparent
                    title={i18n.translate(
                      'AdminHTML.Analytics.SearchAnalytics.topSearchesWithoutClicks_tableTitle'
                    )}
                    linkDetail={routes.analytics.searchesWithoutClicks()}
                    dataTableProps={{
                      headings: [
                        i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                        i18n.translate('AdminHTML.Analytics.Common.searchTerm'),
                        i18n.translate('AdminHTML.Analytics.Common.totalSearches_without_clicks')
                      ],
                      rows: this.buildFallbackDataAnalyticsTable(
                        topSearchTermsWithoutClick
                          .slice(0, TOP_ITEM_IN_OVERVIEW)
                          .map((item, index) => [
                            index + 1,
                            item.searchTerm,
                            formatNumberInThousand(item.totalSearchesWithoutClicks)
                          ])
                      ),
                      columnContentTypes: ['text', 'text', 'numeric']
                    }}
                  />
                }
              />
            </Grid.Cell>
            {/* Searches without clicks */}

            {/* Searches without results */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <AnalyticsChartTable
                inverse
                loading={searchesWithoutResultRateLoading || searchTermsWithoutResultLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.SearchAnalytics.searchesWithoutResults_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.Common.tooltip_ChartSearchesWithoutResult'
                )}
                total={searchesWithoutResultRatePercent}
                percentGrowthChange={searchesWithoutResultRatePercentChange}
                compare={compare}
                chart={
                  <Chart
                    isLimitxAxis
                    className="Search-Without-result"
                    transparent
                    type="line"
                    height="270px"
                    suffixOfValue="%"
                    title={i18n.translate('AdminHTML.Analytics.Common.chart')}
                    series={searchesWithoutResultRateSeries}
                    categories={searchesWithoutResultRateCategories}
                  />
                }
                table={
                  <AnalyticsTable
                    smallRow
                    transparent
                    title={i18n.translate(
                      'AdminHTML.Analytics.SearchAnalytics.topSearchesWithoutResults_tableTitle'
                    )}
                    linkDetail={routes.analytics.searchesWithoutResults()}
                    dataTableProps={{
                      headings: [
                        i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                        i18n.translate('AdminHTML.Analytics.Common.searchTerm'),
                        i18n.translate('AdminHTML.Analytics.Common.totalSearches_without_results')
                      ],
                      rows: this.buildFallbackDataAnalyticsTable(
                        searchTermsWithoutResult
                          .slice(0, TOP_ITEM_IN_OVERVIEW)
                          .map((item, index) => [
                            index + 1,
                            item.searchTerm,
                            formatNumberInThousand(item.totalSearchesWithoutResults)
                          ])
                      ),
                      columnContentTypes: ['text', 'text', 'numeric']
                    }}
                  />
                }
              />
            </Grid.Cell>
            {/* Searches without results */}

            {/* Product performance */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
              <div className="BC-table-product-performance">
                <AnalyticsTable
                  title={i18n.translate(
                    'AdminHTML.Analytics.SearchAnalytics.productPerformance_cardTitle'
                  )}
                  tooltip={i18n.translate(
                    'AdminHTML.Analytics.Common.tooltip_TableProductPerformance'
                  )}
                  loading={topProductsOnSearchLoading}
                  linkDetail={routes.analytics.productPerformance()}
                  dataTableProps={{
                    headings: [
                      i18n.translate('AdminHTML.Analytics.Common.numberOfOrder'),
                      '',
                      i18n.translate(
                        'AdminHTML.Analytics.SearchAnalytics.productPerformance_productHeader'
                      ),
                      i18n.translate(
                        'AdminHTML.Analytics.SearchAnalytics.productPerformance_clickCountHeader'
                      )
                    ],
                    rows: this.buildFallbackDataAnalyticsTable(
                      topProductsOnSearch.slice(0, TOP_ITEM_IN_OVERVIEW).map((item, index) => {
                        const { productName, clickCount, productImages } = item;
                        let image = _.isEmpty(productImages)
                          ? ''
                          : productImages[Object.keys(productImages)[0]];
                        image = _.isEmpty(image) ? noProductImage : optimizeImage(image, '40x40');

                        return [
                          index + 1,
                          <Thumbnail source={image} alt="product" size="small" />,
                          productName,
                          formatNumberInThousand(clickCount)
                        ];
                      }),
                      /* isProductPerformance = */ true
                    ),
                    columnContentTypes: ['text', 'numeric', 'text', 'numeric']
                  }}
                />
              </div>
            </Grid.Cell>
            {/* Product performance */}
          </Grid>
        </div>
      </div>
    );
  }

  buildChartData = (
    sourceDataArr,
    comparedDataArr = [],
    displayedSourceDate,
    displayedComparedDate,
    customFieldTotal, // Priority field here to get data for showing on chart
    isLimitxAxis
  ) => {
    const { compare, timezone } = this.props;

    let categories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    // Process limit x axis max 3,4 points
    if (isLimitxAxis && isLimitxAxis < categories.length) {
      categories = limitXAxisPoint(categories, MAX_NUM_X_AXIS_POINTS_SEARCH_CHART);
    }

    const fullCategories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    const data = sourceDataArr.map(item =>
      customFieldTotal ? item[customFieldTotal] : item.total
    );

    const series = [
      {
        name: displayedSourceDate,
        data: data,
        fullCategories
      }
    ];

    if (compare) {
      const comparedData = comparedDataArr.map(item =>
        customFieldTotal ? item[customFieldTotal] : item.total
      );
      series.push({
        name: displayedComparedDate,
        data: comparedData
      });
    }

    return {
      series,
      categories
    };
  };

  buildFallbackDataAnalyticsTable = (builtData: any[], isProductPerformance = false) => {
    if (_.isEmpty(builtData)) {
      return [...Array(5)].map((item, index) =>
        isProductPerformance ? [index + 1, '', 'N/A', 0] : [index + 1, 'N/A', 0]
      );
    }
    return builtData;
  };

  fetchAll = (fromDate, toDate, compare) => {
    const {
      searchRevenueLoading,
      searchTermsWithoutResultLoading,
      searchesWithoutResultRateLoading,
      conversionRateLoading,
      totalSearchesLoading,
      topSearchTermsLoading,
      topSearchTermsWithClickLoading,
      topProductsOnSearchLoading,
      searchesClickRateLoading,
      totalSearchesWithoutClickLoading,
      topSearchTermsWithoutClickLoading
    } = this.props;

    if (!searchRevenueLoading) {
      this.getTotalSearchRevenue(fromDate, toDate, compare);
    }

    if (!topSearchTermsLoading) {
      this.getListTopSearchTerms(fromDate, toDate, compare);
    }

    if (!searchesWithoutResultRateLoading) {
      this.getTotalSearchesWithoutResult(fromDate, toDate, compare);
    }

    if (!searchTermsWithoutResultLoading) {
      this.getTopSearchTermsWithoutResult(fromDate, toDate, compare); // TODO: Update compare here later if there's any changes
    }

    if (!searchesClickRateLoading) {
      this.getSearchesClickRate(fromDate, toDate, compare);
    }

    if (!totalSearchesWithoutClickLoading) {
      this.getTotalSearchesWithoutClick(fromDate, toDate, compare);
    }

    if (!topSearchTermsWithoutClickLoading) {
      this.getTopSearchTermsWithoutClick(fromDate, toDate, compare);
    }

    if (!conversionRateLoading) {
      this.getConversionRate(fromDate, toDate, compare);
    }

    if (!totalSearchesLoading) {
      this.getTotalSearches(fromDate, toDate, compare);
    }

    if (!topSearchTermsWithClickLoading) {
      this.getTopSearchTermsWithClick(fromDate, toDate, compare);
    }

    if (!topProductsOnSearchLoading) {
      this.getTopSearchProductPerformance(fromDate, toDate, compare);
    }
  };

  getTopSearchTermsWithoutClick = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare: false });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS_WITHOUT_CLICK,
      executionIds
    );

    this.props.getTopSearchTermsWithoutClick(fromDate, toDate, queryExecutionId);
  };

  getSearchesClickRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.SEARCHES_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.SEARCHES_RATE_COMPARED,
      executionIds
    );

    this.props.getSearchesClickRate(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getTotalSearchesWithoutClick = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_SEARCHES_WITHOUT_CLICK,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_SEARCHES_WITHOUT_CLICK_COMPARED,
      executionIds
    );

    this.props.getTotalSearchesWithoutClick(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getTotalSearchRevenue = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.GET_TOTAL_SEARCH_REVENUE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.GET_TOTAL_SEARCH_REVENUE_COMPARED,
      executionIds
    );

    this.props.getTotalSearchRevenue(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getListTopSearchTerms = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getListTopSearchTerms(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getTotalSearchesWithoutResult = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.SEARCHES_WITHOUT_RESULT_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.SEARCHES_WITHOUT_RESULT_RATE_COMPARED,
      executionIds
    );

    this.props.getTotalSearchesWithoutResult(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getTopSearchTermsWithoutResult = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.SEARCH_TERMS_WITHOUT_RESULT;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getTopSearchTermsWithoutResult(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getTopSearchTermsWithClick = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS_WITH_CLICK;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getTopSearchTermsWithClick(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getConversionRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.CONVERSION_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.CONVERSION_RATE_COMPARED,
      executionIds
    );

    this.props.getConversionRate(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getTotalSearches = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_SEARCHES,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.TOTAL_SEARCHES_COMPARED,
      executionIds
    );

    this.props.getTotalSearches(fromDate, toDate, compare, queryExecutionId, comparedExecutionId);
  };

  getTopSearchProductPerformance = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.SEARCH_PRODUCT_PERFORMANCES;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getTopSearchProductPerformance(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  processUpdateExecutionId = (props, nextProps) => {
    this.processTopSearchTermsExecutionId(props, nextProps);
    this.processTotalRevenueExecutionId(props, nextProps);
    this.processConversionRateExecutionId(props, nextProps);
    this.processClickRateExecutionIdOnSearch(props, nextProps);
    this.processTotalSearchesExecutionId(props, nextProps);
    this.processTopSearchTermsWithClickExecutionId(props, nextProps);
    this.processTotalSearchesWithoutClickExecutionId(props, nextProps);
    this.processTopSearchTermsWithoutClickExecutionId(props, nextProps);
    this.processTotalSearchesWithoutResultExecutionId(props, nextProps);
    this.processTopSearchTermsWithoutResultExecutionId(props, nextProps);
    this.processProductPerformanceExecutionId(props, nextProps);
  };

  processTotalRevenueExecutionId = (props, nextProps) => {
    const {
      searchRevenueExecutionId: executionId,
      searchRevenueComparedExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      searchRevenueExecutionId: nextExecutionId,
      searchRevenueComparedExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.GET_TOTAL_SEARCH_REVENUE,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.GET_TOTAL_SEARCH_REVENUE_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processTopSearchTermsExecutionId = (props, nextProps) => {
    const { topSearchTermsQueryExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      topSearchTermsQueryExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS,
        executionId: nextExecutionId
      });
    }
  };

  processTopSearchTermsWithoutClickExecutionId = (props, nextProps) => {
    const { topSearchTermsWithoutClickExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      topSearchTermsWithoutClickExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS_WITHOUT_CLICK,
        executionId: nextExecutionId
      });
    }
  };

  processTotalSearchesWithoutClickExecutionId = (props, nextProps) => {
    const {
      totalSearchesWithoutClickExecutionId: executionId,
      totalSearchesWithoutClickComparedExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalSearchesWithoutClickExecutionId: nextExecutionId,
      totalSearchesWithoutClickComparedExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_SEARCHES_WITHOUT_CLICK,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_SEARCHES_WITHOUT_CLICK_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processClickRateExecutionIdOnSearch = (props, nextProps) => {
    const {
      searchesClickRateExecutionId: executionId,
      searchesClickRateComparedExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      searchesClickRateExecutionId: nextExecutionId,
      searchesClickRateComparedExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCHES_RATE,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCHES_RATE_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processTotalSearchesWithoutResultExecutionId = (props, nextProps) => {
    const {
      searchesWithoutResultRateQueryExecutionId: executionId,
      searchesWithoutResultRateComparedQueryExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      searchesWithoutResultRateQueryExecutionId: nextExecutionId,
      searchesWithoutResultRateComparedQueryExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCHES_WITHOUT_RESULT_RATE,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCHES_WITHOUT_RESULT_RATE_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processTopSearchTermsWithoutResultExecutionId = (props, nextProps) => {
    const { searchTermsWithoutResultQueryExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      searchTermsWithoutResultQueryExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false // TODO: modify here if there's any updates in the future
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCH_TERMS_WITHOUT_RESULT,
        executionId: nextExecutionId
      });
    }
  };

  /**
   * Process search terms execution id
   * @param {*} props
   * @param {*} nextProps
   */
  processTopSearchTermsWithClickExecutionId = (props, nextProps) => {
    const { topSearchTermsWithClickExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      topSearchTermsWithClickExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOP_SEARCH_TERMS_WITH_CLICK,
        executionId: nextExecutionId
      });
    }
  };

  /**
   * Process conversion rate execution id
   * @param {*} props
   * @param {*} nextProps
   */
  processConversionRateExecutionId = (props, nextProps) => {
    const {
      conversionRateQueryExecutionId: executionId,
      conversionRateComparedExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      conversionRateQueryExecutionId: nextExecutionId,
      conversionRateComparedExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.CONVERSION_RATE,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.CONVERSION_RATE_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  processTotalSearchesExecutionId = (props, nextProps) => {
    const {
      totalSearchesQueryExecutionId: executionId,
      totalSearchesComparedExecutionId: comparedExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      totalSearchesQueryExecutionId: nextExecutionId,
      totalSearchesComparedExecutionId: nextComparedExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_SEARCHES,
        executionId: nextExecutionId
      });
    }

    if (nextComparedExecutionId && comparedExecutionId !== nextComparedExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.TOTAL_SEARCHES_COMPARED,
        executionId: nextComparedExecutionId
      });
    }
  };

  /**
   * Process product performance execution id
   * @param {*} props
   * @param {*} nextProps
   */
  processProductPerformanceExecutionId = (props, nextProps) => {
    const { topProductsOnSearchExecutionId: executionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      topProductsOnSearchExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.SEARCH_PRODUCT_PERFORMANCES,
        executionId: nextExecutionId
      });
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),

  // Get searches without result rate
  getTotalSearchesWithoutResult: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalSearchesWithoutResult(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getTotalSearchRevenue: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalSearchRevenue(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  // Get search terms without result
  getListTopSearchTerms: (from, to, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getListTopSearchTerms(
        from,
        to,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),

  // Get search terms without result
  getTopSearchTermsWithoutResult: (from, to, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getTopSearchTermsWithoutResult(
        from,
        to,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),

  // Get searches click rate
  getSearchesClickRate: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getSearchesClickRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),

  // Get searches without click rate
  getTotalSearchesWithoutClick: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalSearchesWithoutClick(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),

  getTopSearchTermsWithoutClick: (from, to, queryExecutionId) =>
    dispatch(
      AnalyticsActions.getTopSearchTermsWithoutClick(from, to, queryExecutionId, ownProps.i18n)
    ),

  getConversionRate: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getConversionRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),

  getTotalSearches: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getTotalSearches(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),

  getTopSearchTermsWithClick: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getTopSearchTermsWithClick(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),

  getTopSearchProductPerformance: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getTopProductsOnSearch(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),

  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    timezone: _.get(state.oauth, 'tenantApp.externalInfo.iana_timezone', ''),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Searches without result rate
    searchesWithoutResultRate: _.get(state.analyticsV2, 'searchesWithoutResultRate', []),
    searchesWithoutResultRateCompared: _.get(
      state.analyticsV2,
      'searchesWithoutResultRateCompared',
      []
    ),
    searchesWithoutResultRateLoading: _.get(
      state.analyticsV2,
      'searchesWithoutResultRateLoading',
      false
    ),
    searchesWithoutResultRateQueryExecutionId: _.get(
      state.analyticsV2,
      'searchesWithoutResultRateQueryExecutionId',
      ''
    ),
    searchesWithoutResultRateComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'searchesWithoutResultRateComparedQueryExecutionId',
      ''
    ),
    searchesWithoutResultRatePercent: _.get(
      state.analyticsV2,
      'searchesWithoutResultRatePercent',
      ''
    ),
    searchesWithoutResultRatePercentChange: _.get(
      state.analyticsV2,
      'searchesWithoutResultRatePercentChange',
      0
    ),

    // Search terms without result
    searchTermsWithoutResult: _.get(state.analyticsV2, 'searchTermsWithoutResult', []),
    searchTermsWithoutResultLoading: _.get(
      state.analyticsV2,
      'searchTermsWithoutResultLoading',
      false
    ),
    searchTermsWithoutResultQueryExecutionId: _.get(
      state.analyticsV2,
      'searchTermsWithoutResultQueryExecutionId'
    ),

    // Searches click rate
    searchesClickRate: _.get(state.analyticsV2, 'searchesClickRate', []),
    searchesClickRateCompared: _.get(state.analyticsV2, 'searchesClickRateCompared', []),
    searchesClickRateLoading: _.get(state.analyticsV2, 'searchesClickRateLoading', false),
    searchesClickRatePercent: _.get(state.analyticsV2, 'searchesClickRatePercent', ''),
    searchesClickRatePercentChange: _.get(state.analyticsV2, 'searchesClickRatePercentChange', 0),
    searchesClickRateExecutionId: _.get(state.analyticsV2, 'searchesClickRateExecutionId'),
    searchesClickRateComparedExecutionId: _.get(
      state.analyticsV2,
      'searchesClickRateComparedExecutionId'
    ),

    // Searches without click rate
    totalSearchesWithoutClick: _.get(state.analyticsV2, 'totalSearchesWithoutClick', []),
    totalSearchesWithoutClickCompared: _.get(
      state.analyticsV2,
      'totalSearchesWithoutClickCompared',
      []
    ),
    totalSearchesWithoutClickLoading: _.get(
      state.analyticsV2,
      'totalSearchesWithoutClickLoading',
      false
    ),
    totalSearchesWithoutClickPercent: _.get(
      state.analyticsV2,
      'totalSearchesWithoutClickPercent',
      ''
    ),
    totalSearchesWithoutClickPercentChange: _.get(
      state.analyticsV2,
      'totalSearchesWithoutClickPercentChange',
      0
    ),
    totalSearchesWithoutClickExecutionId: _.get(
      state.analyticsV2,
      'totalSearchesWithoutClickExecutionId'
    ),
    totalSearchesWithoutClickComparedExecutionId: _.get(
      state.analyticsV2,
      'totalSearchesWithoutClickComparedExecutionId'
    ),

    // Total search revenue
    searchRevenueLoading: _.get(state.analyticsV2, 'searchRevenueLoading', false),
    searchRevenue: _.get(state.analyticsV2, 'searchRevenue', []),
    searchRevenueCompared: _.get(state.analyticsV2, 'searchRevenueCompared', []),
    searchRevenueTotal: _.get(state.analyticsV2, 'searchRevenueTotal', 0),
    searchRevenuePercentChange: _.get(state.analyticsV2, 'searchRevenuePercentChange', 0),
    searchRevenueExecutionId: _.get(state.analyticsV2, 'searchRevenueExecutionId'),
    searchRevenueComparedExecutionId: _.get(state.analyticsV2, 'searchRevenueComparedExecutionId'),

    // Search terms without click
    topSearchTermsWithoutClick: _.get(state.analyticsV2, 'topSearchTermsWithoutClick', []),
    topSearchTermsWithoutClickLoading: _.get(
      state.analyticsV2,
      'topSearchTermsWithoutClickLoading',
      false
    ),
    topSearchTermsWithoutClickExecutionId: _.get(
      state.analyticsV2,
      'topSearchTermsWithoutClickExecutionId'
    ),

    // Conversion rate
    conversionRate: _.get(state.analyticsV2, 'conversionRate', []),
    conversionRateCompared: _.get(state.analyticsV2, 'conversionRateCompared', []),
    conversionRateLoading: _.get(state.analyticsV2, 'conversionRateLoading', false),
    conversionRatePercent: _.get(state.analyticsV2, 'conversionRatePercent', ''),
    conversionRatePercentChange: _.get(state.analyticsV2, 'conversionRatePercentChange', 0),
    conversionRateQueryExecutionId: _.get(state.analyticsV2, 'conversionRateQueryExecutionId'),
    conversionRateComparedExecutionId: _.get(
      state.analyticsV2,
      'conversionRateComparedExecutionId'
    ),

    // Total searches
    totalSearches: _.get(state.analyticsV2, 'totalSearches', []),
    totalSearchesCompared: _.get(state.analyticsV2, 'totalSearchesCompared', []),
    totalSearchesLoading: _.get(state.analyticsV2, 'totalSearchesLoading', false),
    totalSearchesNumber: _.get(state.analyticsV2, 'totalSearchesNumber', 0),
    totalSearchesPercentChange: _.get(state.analyticsV2, 'totalSearchesPercentChange', 0),
    totalSearchesQueryExecutionId: _.get(state.analyticsV2, 'totalSearchesQueryExecutionId'),
    totalSearchesComparedExecutionId: _.get(state.analyticsV2, 'totalSearchesComparedExecutionId'),

    // Top search terms
    topSearchTerms: _.get(state.analyticsV2, 'topSearchTerms', []),
    topSearchTermsLoading: _.get(state.analyticsV2, 'topSearchTermsLoading', false),
    topSearchTermsQueryExecutionId: _.get(state.analyticsV2, 'topSearchTermsQueryExecutionId'),

    topSearchTermsWithClick: _.get(state.analyticsV2, 'topSearchTermsWithClick', []),
    topSearchTermsWithClickLoading: _.get(
      state.analyticsV2,
      'topSearchTermsWithClickLoading',
      false
    ),
    topSearchTermsWithClickExecutionId: _.get(
      state.analyticsV2,
      'topSearchTermsWithClickExecutionId'
    ),

    // Product performance
    topProductsOnSearch: _.get(state.analyticsV2, 'topProductsOnSearch', []),
    topProductsOnSearchLoading: _.get(state.analyticsV2, 'topProductsOnSearchLoading', false),
    topProductsOnSearchExecutionId: _.get(state.analyticsV2, 'topProductsOnSearchExecutionId'),

    moneyFormat: _.get(state.oauth, 'tenantApp.externalInfo.money_format', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SearchAnalyticsOverview));
